/**
 * Interstitial API
 *
 * Network-only functions for flight interstitial (fare selection) operations.
 * NO Redux imports allowed in this file.
 *
 * Uses flightsApiFetch which automatically adds:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
import {
  type FlightsReviewInterstitialTemplate,
  type ApiErrorResponse,
  type PopupErrorDetails,
} from '@contracts';
import { extractPopupErrorDetails } from '@/features/flights/utils/apiErrorUtils';
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildUrl, buildHeaders, buildQueryParams } from '@/api/utils';
import { logger } from '@/shared/utils/logger';
import type { ReviewRequestParams } from '../types';

// ============================================
// Error Types
// ============================================

/**
 * Structured error details from API response.
 * Used for displaying error popups with meaningful messages.
 * @deprecated Use PopupErrorDetails from @contracts instead
 */
export type InterstitialErrorDetails = PopupErrorDetails;

// ============================================
// Error Classes
// ============================================

export class InterstitialError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
    public readonly errorDetails?: PopupErrorDetails,
  ) {
    super(message);
    this.name = 'InterstitialError';
  }
}

export function parseContextHashKeys(contextHashKey: string): { crid: string; rkey: string } {
  const contextHashKeys = contextHashKey.split('~~~');
  const crid = contextHashKeys[0]?.split('|')[0];
  const rkey = contextHashKeys
    .map((contextHashKey) => {
      const [, rkeyPart] = contextHashKey.split('|');
      return rkeyPart ?? '';
    })
    .join('~~~');
  return { crid: crid ?? '', rkey: rkey ?? '' };
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch flight interstitial (review) details from the API
 *
 * Uses common flights API wrapper with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchFlightInterstitial(
  params: ReviewRequestParams,
): Promise<FlightsReviewInterstitialTemplate> {
  const queryParams = buildQueryParams({
    crid: params.crId,
    rkey: params.contextHashKey,
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.REVIEW, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    // Always try to parse response body to get error details
    const data = (await response.json()) as FlightsReviewInterstitialTemplate & ApiErrorResponse;

    // Check if API returned an error in the response body
    const errorDetails = extractPopupErrorDetails(data);
    if (errorDetails) {
      throw new InterstitialError(errorDetails.errorMessage, response.status, data, errorDetails);
    }

    if (!response.ok) {
      throw new InterstitialError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    logger.warn('[InterstitialClient] Received template:', data);
    return data;
  } catch (error) {
    if (error instanceof InterstitialError) {
      throw error;
    }

    logger.error('[InterstitialClient] Request failed:', error);
    throw new InterstitialError('Failed to fetch flight interstitial', undefined, error);
  }
}

// ============================================
// Backward Compatibility Aliases (Deprecated)
// ============================================

/**
 * @deprecated Use InterstitialError instead
 */
export const ReviewError = InterstitialError;

/**
 * @deprecated Use fetchFlightInterstitial instead
 */
export const fetchFlightReview = fetchFlightInterstitial;
